/**
 * API配置 - 根据APP_CONFIG.BACKEND_TARGET选择后端服务器
 */

interface APIConfig {
  baseUrl: string;
  timeout: number;
  enableLogging: boolean;
  retryAttempts: number;
  retryDelay: number;
}

const DEV_CONFIG: APIConfig = {
  baseUrl: Platform.OS === 'web' ? 'http://localhost:3000' : 'http://10.0.2.2:3000', // Web使用localhost，移动端使用模拟器地址
  timeout: 10000, // 10秒超时
  enableLogging: true,
  retryAttempts: 2, // 开发环境少重试，快速失败
  retryDelay: 1000, // 1秒重试间隔
};

const PROD_CONFIG: APIConfig = {
  baseUrl: 'https://clickmaster-api.onrender.com', // 生产环境 - Render部署
  timeout: 30000, // 30秒超时（Render冷启动可能较慢）
  enableLogging: false,
  retryAttempts: 3, // 生产环境多重试几次
  retryDelay: 2000, // 2秒重试间隔
};

// 导入APP配置
import { APP_CONFIG } from './version';
import { Platform } from 'react-native';

// 根据APP_CONFIG.BACKEND_TARGET选择配置
export const API_CONFIG: APIConfig = APP_CONFIG.BACKEND_TARGET === 'development' ? DEV_CONFIG : PROD_CONFIG;

// 导出常用配置项
export const {
  baseUrl: API_BASE_URL,
  timeout: API_TIMEOUT,
  enableLogging: ENABLE_API_LOGGING,
  retryAttempts: API_RETRY_ATTEMPTS,
  retryDelay: API_RETRY_DELAY,
} = API_CONFIG;

// 环境信息
export const ENVIRONMENT = {
  isDevelopment: APP_CONFIG.BACKEND_TARGET === 'development',
  isProduction: APP_CONFIG.BACKEND_TARGET === 'production',
  name: APP_CONFIG.BACKEND_TARGET === 'development' ? 'Development' : 'Production',
  backendTarget: APP_CONFIG.BACKEND_TARGET,
};

// 网络请求包装器，带有重试和超时处理
export const apiRequest = async <T>(
  url: string,
  options: RequestInit = {}
): Promise<T> => {
  let attempt = 0;
  const maxAttempts = API_CONFIG.retryAttempts + 1;

  while (attempt < maxAttempts) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);

      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (ENABLE_API_LOGGING) {
        console.log(`✅ API Request successful: ${url}`, {
          status: response.status,
          attempt: attempt + 1,
          data: APP_CONFIG.ENABLE_DEBUG_LOGS ? data : '[hidden in production]',
        });
      }

      return data;
    } catch (error) {
      attempt++;

      if (ENABLE_API_LOGGING) {
        console.warn(`⚠️ API Request failed (attempt ${attempt}/${maxAttempts}): ${url}`, error);
      }

      if (attempt >= maxAttempts) {
        if (ENABLE_API_LOGGING) {
          console.error(`❌ API Request failed after ${maxAttempts} attempts: ${url}`, error);
        }
        throw error;
      }

      // 等待重试延迟
      await new Promise(resolve => setTimeout(resolve, API_CONFIG.retryDelay));
    }
  }

  throw new Error('Unexpected error in API request');
};

// 健康检查功能
export const checkAPIHealth = async (): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/health`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      const data = await response.json();
      const isHealthy = data.status === 'healthy';

      if (ENABLE_API_LOGGING) {
        console.log(isHealthy ? '✅ API Health Check: Server Healthy' : '⚠️ API Health Check: Server Unhealthy', {
          httpStatus: response.status,
          serverStatus: data.status,
          service: data.service,
          timestamp: data.timestamp,
          url: `${API_BASE_URL}/health`,
        });
      }

      return isHealthy;
    } else {
      if (ENABLE_API_LOGGING) {
        console.log('❌ API Health Check: HTTP Error', {
          status: response.status,
          url: `${API_BASE_URL}/health`,
        });
      }
      return false;
    }
  } catch (error) {
    if (ENABLE_API_LOGGING) {
      console.error('❌ API Health Check: Network Error', error);
    }
    return false;
  }
};

// 调试信息
if (APP_CONFIG.ENABLE_DEBUG_LOGS) {
  console.log('🔧 API Configuration:', {
    environment: ENVIRONMENT.name,
    backendTarget: ENVIRONMENT.backendTarget,
    baseUrl: API_CONFIG.baseUrl,
    timeout: `${API_CONFIG.timeout}ms`,
    enableLogging: API_CONFIG.enableLogging,
    retryAttempts: API_CONFIG.retryAttempts,
    retryDelay: `${API_CONFIG.retryDelay}ms`,
    healthCheckEnabled: APP_CONFIG.ENABLE_HEALTH_CHECK,
  });

  // 自动进行健康检查
  setTimeout(() => {
    checkAPIHealth().then(isHealthy => {
      if (isHealthy) {
        console.log(`🚀 Backend API is ready (${ENVIRONMENT.backendTarget})`);
      } else {
        console.warn(`⚠️ Backend API health check failed (${ENVIRONMENT.backendTarget}). Make sure your backend server is running on`, API_BASE_URL);
      }
    });
  }, 1000);
}
